/**
 * The three long-standing language defects, fixed in one place.
 *
 *   1. **`sp` never existed.** The legacy switch changed the language to `sp`
 *      while the folder on disk is `public/locales/esp`, so every request for
 *      Spanish 404'd and i18next quietly fell back to English. The code is
 *      `esp` everywhere now, and a `sp` left in `localStorage` from before is
 *      migrated on boot.
 *   2. **`<html>` had no `lang`.** A screen reader guessed the language of the
 *      whole page and per-language CSS (`:lang(zh)` in `theme/v2.css`) never
 *      matched. It is set from the active language now — and to a real BCP-47
 *      tag, not to our internal code: `cn` is Simplified Chinese, `zh-Hans`,
 *      and `esp` is `es`.
 *   3. **Chinese had no font.** `theme/v2.css` already asks for "Noto Sans SC"
 *      in both stacks, but nothing ever loaded the face, so the page rendered in
 *      whatever the system happened to pick. It is loaded here, and only once
 *      Chinese is actually the active language — an English or Spanish visitor
 *      pays nothing.
 *
 * This module is shared with the legacy viewer (`src/index.tsx` starts the
 * sync), so it deliberately holds nothing but the language plumbing: no strings,
 * no React, and no import of the label tree.
 */

import i18next from "i18next";

export type V2LanguageCode = "en" | "cn" | "esp" | "hi";

export interface V2Language {
  /** What i18next uses and what the folder under `public/locales` is called. */
  code: V2LanguageCode;
  /** Two or three letters for the control. */
  short: string;
  /** The language in its own language. */
  name: string;
  /** What goes on `<html lang>` — a real BCP-47 tag. */
  html: string;
}

export const V2_LANGUAGES: readonly V2Language[] = [
  { code: "en", short: "EN", name: "English", html: "en" },
  { code: "cn", short: "CN", name: "中文", html: "zh-Hans" },
  { code: "esp", short: "ESP", name: "Español", html: "es" },
  { code: "hi", short: "HI", name: "हिन्दी", html: "hi" },
];

/** `i18nextLng` values that must land on one of the three folders we ship. */
const ALIASES: Record<string, V2LanguageCode> = {
  sp: "esp",
  es: "esp",
  spa: "esp",
  zh: "cn",
  cmn: "cn",
  hin: "hi",
  en: "en",
  eng: "en",
};

export const LANGUAGE_STORAGE_KEY = "i18nextLng";

/** `es-AR` → `esp`, `zh-Hans-CN` → `cn`, anything unknown → `en`. */
export function normaliseLanguage(raw: string | null | undefined): V2LanguageCode {
  if (!raw) return "en";
  const lower = String(raw).toLowerCase();
  for (const language of V2_LANGUAGES) {
    if (lower === language.code) return language.code;
  }
  const base = lower.split("-")[0];
  if (ALIASES[lower]) return ALIASES[lower];
  if (ALIASES[base]) return ALIASES[base];
  return "en";
}

/** The BCP-47 tag for `<html lang>`. */
export function htmlLangOf(raw: string | null | undefined): string {
  const code = normaliseLanguage(raw);
  for (const language of V2_LANGUAGES) {
    if (language.code === code) return language.html;
  }
  return "en";
}

/* ------------------------------------------------------------------ */
/* The Chinese face, loaded on demand                                   */
/* ------------------------------------------------------------------ */

const CJK_LINK_ID = "smrtstats-cjk-font";
const CJK_STYLE_ID = "smrtstats-cjk-font-stack";

const CJK_STACK = '"Noto Sans SC", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", system-ui, sans-serif';

/**
 * Adds the webfont once, the first time Chinese becomes the active language.
 * `.v2-root` already names "Noto Sans SC" in both of its stacks; the style
 * element is what gives the legacy page the same face, scoped to `lang="zh…"`
 * so nothing else on the site changes.
 */
function loadChineseFont(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(CJK_LINK_ID) === null) {
    const link = document.createElement("link");
    link.id = CJK_LINK_ID;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }
  if (document.getElementById(CJK_STYLE_ID) === null) {
    const style = document.createElement("style");
    style.id = CJK_STYLE_ID;
    style.textContent = `html[lang^="zh"] body { font-family: ${CJK_STACK}; }`;
    document.head.appendChild(style);
  }
}

/* ------------------------------------------------------------------ */
/* The Devanagari face, loaded on demand                                */
/* ------------------------------------------------------------------ */

const DEVANAGARI_LINK_ID = "smrtstats-devanagari-font";
const DEVANAGARI_STYLE_ID = "smrtstats-devanagari-font-stack";

const DEVANAGARI_STACK =
  '"Noto Sans Devanagari", "Nirmala UI", "Kohinoor Devanagari", "Mangal", system-ui, sans-serif';

/**
 * Same shape as the Chinese loader, for the same reason: Windows and older
 * Android ship a Devanagari face that sits badly beside Archivo, and neither
 * "IBM Plex Sans" nor "Archivo" covers the script at all — without this the
 * browser picks whatever it finds and the matras land where they please.
 */
function loadDevanagariFont(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(DEVANAGARI_LINK_ID) === null) {
    const link = document.createElement("link");
    link.id = DEVANAGARI_LINK_ID;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }
  if (document.getElementById(DEVANAGARI_STYLE_ID) === null) {
    const style = document.createElement("style");
    style.id = DEVANAGARI_STYLE_ID;
    style.textContent = `html[lang^="hi"] body { font-family: ${DEVANAGARI_STACK}; }`;
    document.head.appendChild(style);
  }
}

/* ------------------------------------------------------------------ */
/* Applying a language to the document                                  */
/* ------------------------------------------------------------------ */

/** Sets `<html lang>` and, for Chinese only, loads the face. */
export function applyDocumentLanguage(raw: string | null | undefined): void {
  if (typeof document === "undefined") return;
  const code = normaliseLanguage(raw);
  const tag = htmlLangOf(code);
  if (document.documentElement.getAttribute("lang") !== tag) {
    document.documentElement.setAttribute("lang", tag);
  }
  if (code === "cn") loadChineseFont();
  if (code === "hi") loadDevanagariFont();
}

/**
 * Rewrites a stored `sp` — the code the old switch wrote, which never matched a
 * folder — to `esp`. Returns the language the app should start in.
 */
export function migrateStoredLanguage(): V2LanguageCode {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    /* private mode: fall through to the default */
  }
  const code = normaliseLanguage(stored);
  if (stored !== code) {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {
      /* private mode: the detector keeps it in memory for this session */
    }
  }
  return code;
}

/**
 * Keeps `<html lang>` on the active language for as long as the app lives.
 * Called once from `src/index.tsx`, before the first render, so both viewers
 * are covered. Returns an unsubscribe function for tests.
 */
export function startLanguageSync(): () => void {
  const sync = (): void => {
    const raw = i18next.language;
    const code = normaliseLanguage(raw);
    /* A code we do not ship a folder for — `sp` from the old switch, `en-US`
       from the browser — is moved onto one we do, which also stops the 404 for
       `/locales/<that>/translation.json`. The change fires this back. */
    if (i18next.isInitialized && typeof raw === "string" && raw !== code) {
      void i18next.changeLanguage(code);
      return;
    }
    applyDocumentLanguage(code);
  };
  if (i18next.isInitialized) sync();
  else applyDocumentLanguage(migrateStoredLanguage());
  i18next.on("initialized", sync);
  i18next.on("languageChanged", sync);
  return () => {
    i18next.off("initialized", sync);
    i18next.off("languageChanged", sync);
  };
}
